import type { SantaGesture } from '@/server/domain/santa-chat'

export interface SantaGestureClip {
  /** mp4 vertical sin audio que empieza y acaba en el póster. */
  videoSrc: string
  /** Audio propio del gesto, que suena antes de la voz (solo `laugh`). */
  audioSrc?: string
}

/**
 * Gestos con clip propio, que se ven una vez: la risa (con su audio, antes de la voz), el saludo
 * y la despedida (al empezar la voz, porque en sus clips Papá Noel habla) y pensar (mientras
 * espera). `talk`, `surprise` y `wink` no tienen clip: con voz, Papá Noel habla en bucle.
 */
export type SantaOneShotGesture = Extract<SantaGesture, 'laugh' | 'wave' | 'goodbye' | 'think'>

/** Fotograma de reposo: el mismo con el que empiezan y acaban todos los clips. */
export const SANTA_IDLE_POSTER = '/santa-chat/gestures/poster.jpg'

/** Reposo en bucle (solo respira y parpadea). */
export const SANTA_IDLE_LOOP = '/santa-chat/gestures/idle.mp4'

/** Habla sin parar: suena en bucle mientras dura su voz, sin sincronía de labios. */
export const SANTA_TALK_LOOP = '/santa-chat/gestures/talk-loop.mp4'

/** «Llámame», mudo: suena en bucle bajo el botón de videollamada cuando el chat ofrece llamar. */
export const SANTA_CALL_LOOP = '/santa-chat/gestures/call-loop.mp4'

/** Clips de FastH3 (Reactor) generados desde el póster. La risa tiene además su toma de audio (spike TTS, toma 1). */
export const SANTA_GESTURE_CLIPS: Record<SantaOneShotGesture, SantaGestureClip> = {
  laugh: { videoSrc: '/santa-chat/gestures/laugh.mp4', audioSrc: '/santa-chat/laugh.mp3' },
  wave: { videoSrc: '/santa-chat/gestures/wave.mp4' },
  goodbye: { videoSrc: '/santa-chat/gestures/goodbye.mp4' },
  think: { videoSrc: '/santa-chat/gestures/think.mp4' },
}

export const SANTA_ONE_SHOT_GESTURES = Object.keys(SANTA_GESTURE_CLIPS) as readonly SantaOneShotGesture[]

export function isOneShot(gesture: SantaGesture): gesture is SantaOneShotGesture {
  return Object.hasOwn(SANTA_GESTURE_CLIPS, gesture)
}

/** El clip propio de un gesto, o `null` si no lo tiene (reposo, o habla en bucle). */
export function gestureClip(gesture: SantaGesture): SantaGestureClip | null {
  return isOneShot(gesture) ? SANTA_GESTURE_CLIPS[gesture] : null
}
